import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return (
      d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }) +
      " · " +
      d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })
    );
  } catch {
    return iso;
  }
};

export default function AdminDashboard({ token, onLogout }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | unread | read
  const [selected, setSelected] = useState(null);

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (filter === "unread") params.is_read = false;
      if (filter === "read") params.is_read = true;
      const { data } = await axios.get(`${API}/admin/contacts`, {
        headers,
        params,
      });
      setItems(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired");
        onLogout();
      } else {
        toast.error("Could not load submissions.");
      }
    } finally {
      setLoading(false);
    }
  }, [headers, q, filter, onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleRead = async (item) => {
    try {
      const { data } = await axios.patch(
        `${API}/admin/contacts/${item.id}`,
        { is_read: !item.is_read },
        { headers },
      );
      setItems((prev) => prev.map((x) => (x.id === item.id ? data : x)));
      if (selected?.id === item.id) setSelected(data);
    } catch {
      toast.error("Update failed.");
    }
  };

  const removeItem = async (item) => {
    if (!window.confirm(`Delete enquiry from ${item.name}?`)) return;
    try {
      await axios.delete(`${API}/admin/contacts/${item.id}`, { headers });
      setItems((prev) => prev.filter((x) => x.id !== item.id));
      if (selected?.id === item.id) setSelected(null);
      toast.success("Deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/contacts/export`, {
        headers,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `momenta-contacts-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed.");
    }
  };

  const unreadCount = items.filter((i) => !i.is_read).length;

  return (
    <div
      data-testid="admin-dashboard"
      className="min-h-screen w-full bg-black text-white"
    >
      {/* Header */}
      <header className="sticky top-0 z-30 bg-black/85 backdrop-blur-md border-b border-white/10">
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 py-5 flex items-center justify-between gap-4">
          <div className="flex items-baseline gap-4">
            <a
              href="/"
              className="font-serif italic text-xl sm:text-2xl"
              data-testid="admin-home-link"
            >
              Momenta<span className="not-italic font-light"> Studios.</span>
            </a>
            <span className="eyebrow text-white/50 hidden sm:inline">
              / Admin
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={exportCsv}
              data-testid="admin-export-csv"
              className="hidden sm:inline-flex border border-white/30 hover:border-white px-4 py-2 text-[11px] tracking-[0.3em] uppercase transition-colors"
            >
              Export CSV
            </button>
            <button
              onClick={onLogout}
              data-testid="admin-logout"
              className="text-[11px] tracking-[0.3em] uppercase text-white/60 hover:text-white"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 sm:px-10 py-10">
        {/* Title row */}
        <div className="flex items-end justify-between gap-6 flex-wrap mb-10">
          <div>
            <div className="eyebrow mb-3">§ Inbox</div>
            <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tighter">
              Enquiries
              <em className="italic font-medium">.</em>
            </h1>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
            <div className="text-white/60 text-sm">
              <span className="text-white font-mono">{items.length}</span> total
              · <span className="text-white font-mono">{unreadCount}</span>{" "}
              unread
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 sm:gap-4 mb-6 flex-wrap">
          <div className="flex items-center gap-2">
            {[
              { id: "all", label: "All" },
              { id: "unread", label: "Unread" },
              { id: "read", label: "Read" },
            ].map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                data-testid={`admin-filter-${f.id}`}
                className={`px-4 py-2 text-[11px] tracking-[0.3em] uppercase border transition-colors ${
                  filter === f.id
                    ? "bg-white text-black border-white"
                    : "border-white/20 text-white/70 hover:border-white/50 hover:text-white"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <input
            data-testid="admin-search"
            placeholder="Search name, email, message…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="input-underline flex-1 min-w-[180px] max-w-md"
          />
          <button
            onClick={load}
            data-testid="admin-refresh"
            className="text-[11px] tracking-[0.3em] uppercase text-white/60 hover:text-white"
          >
            Refresh
          </button>
          <button
            onClick={exportCsv}
            data-testid="admin-export-csv-mobile"
            className="sm:hidden border border-white/30 px-4 py-2 text-[11px] tracking-[0.3em] uppercase"
          >
            Export
          </button>
        </div>

        {/* Table */}
        <div className="border border-white/10">
          <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 border-b border-white/10 eyebrow text-white/40">
            <div className="col-span-1">#</div>
            <div className="col-span-3">Name · Email</div>
            <div className="col-span-2">Project</div>
            <div className="col-span-3">Message</div>
            <div className="col-span-2">When</div>
            <div className="col-span-1 text-right">·</div>
          </div>

          {loading && (
            <div className="px-5 py-10 text-center text-white/40 text-sm">
              Loading…
            </div>
          )}

          {!loading && items.length === 0 && (
            <div
              data-testid="admin-empty-state"
              className="px-5 py-16 text-center text-white/40"
            >
              <div className="font-serif italic text-2xl text-white/60 mb-2">
                Quiet inbox.
              </div>
              <div className="text-sm">No enquiries match these filters.</div>
            </div>
          )}

          {!loading &&
            items.map((it, i) => (
              <div
                key={it.id}
                data-testid={`admin-row-${i}`}
                className={`grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-5 py-5 border-b border-white/10 hover:bg-white/5 transition-colors cursor-pointer ${
                  !it.is_read ? "bg-white/[0.02]" : ""
                }`}
                onClick={() => setSelected(it)}
              >
                <div className="md:col-span-1 font-mono text-[11px] text-white/40 flex items-center gap-2">
                  {!it.is_read && (
                    <span className="w-1.5 h-1.5 bg-white rounded-full inline-block" />
                  )}
                  <span>{String(i + 1).padStart(2, "0")}</span>
                </div>
                <div className="md:col-span-3">
                  <div className={!it.is_read ? "text-white" : "text-white/80"}>
                    {it.name}
                  </div>
                  <div className="text-white/40 text-sm font-mono truncate">
                    {it.email}
                  </div>
                </div>
                <div className="md:col-span-2 text-white/60 text-sm">
                  {it.project_type || "-"}
                </div>
                <div className="md:col-span-3 text-white/60 text-sm line-clamp-2">
                  {it.message}
                </div>
                <div className="md:col-span-2 text-white/40 text-[11px] tracking-wider font-mono">
                  {fmtDate(it.created_at)}
                </div>
                <div className="md:col-span-1 flex md:justify-end items-center gap-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleRead(it);
                    }}
                    data-testid={`admin-toggle-read-${i}`}
                    className="text-[10px] tracking-[0.25em] uppercase text-white/50 hover:text-white"
                    title={it.is_read ? "Mark unread" : "Mark read"}
                  >
                    {it.is_read ? "Unread" : "Read"}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeItem(it);
                    }}
                    data-testid={`admin-delete-${i}`}
                    className="text-[10px] tracking-[0.25em] uppercase text-white/40 hover:text-white"
                  >
                    Del
                  </button>
                </div>
              </div>
            ))}
        </div>
      </main>

      {/* Detail drawer */}
      {selected && (
        <div
          data-testid="admin-detail-drawer"
          className="fixed inset-0 z-40 bg-black/80 backdrop-blur-md flex justify-end"
          onClick={() => setSelected(null)}
        >
          <aside
            className="w-full sm:w-[560px] h-full bg-black border-l border-white/15 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-8 sm:p-10">
              <div className="flex items-start justify-between gap-4 mb-8">
                <div className="eyebrow">Enquiry</div>
                <button
                  onClick={() => setSelected(null)}
                  className="text-[11px] tracking-[0.3em] uppercase text-white/60 hover:text-white"
                  data-testid="admin-detail-close"
                >
                  Close ×
                </button>
              </div>

              <h3 className="font-serif text-3xl sm:text-4xl font-light tracking-tight">
                {selected.name}
              </h3>
              <a
                href={`mailto:${selected.email}`}
                className="block mt-2 font-serif italic text-white/70 text-lg link-underline break-all"
              >
                {selected.email}
              </a>

              <dl className="mt-8 space-y-5 text-sm">
                <div>
                  <dt className="eyebrow mb-1">Phone</dt>
                  <dd className="font-mono text-white/80">
                    {selected.phone || "—"}
                  </dd>
                </div>
                <div>
                  <dt className="eyebrow mb-1">Project Type</dt>
                  <dd className="text-white/80">
                    {selected.project_type || "-"}
                  </dd>
                </div>
                <div>
                  <dt className="eyebrow mb-1">Received</dt>
                  <dd className="font-mono text-white/80">
                    {fmtDate(selected.created_at)}
                  </dd>
                </div>
                <div>
                  <dt className="eyebrow mb-2">Message</dt>
                  <dd className="text-white/90 leading-relaxed whitespace-pre-wrap">
                    {selected.message}
                  </dd>
                </div>
              </dl>

              <div className="mt-10 flex flex-wrap gap-3">
                <button
                  onClick={() => toggleRead(selected)}
                  data-testid="admin-detail-toggle-read"
                  className="border border-white/30 hover:border-white px-5 py-3 text-[11px] tracking-[0.3em] uppercase"
                >
                  {selected.is_read ? "Mark Unread" : "Mark Read"}
                </button>
                <a
                  href={`mailto:${selected.email}?subject=Re: your enquiry to Momenta Studios`}
                  data-testid="admin-detail-reply"
                  className="bg-white text-black hover:bg-white/85 px-5 py-3 text-[11px] tracking-[0.3em] uppercase"
                >
                  Reply →
                </a>
                <button
                  onClick={() => removeItem(selected)}
                  data-testid="admin-detail-delete"
                  className="text-[11px] tracking-[0.3em] uppercase text-white/40 hover:text-white ml-auto"
                >
                  Delete
                </button>
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
